import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Inbox, Phone, MessageSquare, Plus, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", group_id: "", message: "" };

function ContactForm({ groups, initial, onSaved }) {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.group_id) return toast.error("Preencha nome, telefone e grupo");
    const payload = { name: form.name, phone: form.phone, group_id: form.group_id, message: form.message || "" };
    try {
      if (isEdit) await api.put(`/contacts/${initial.id}`, payload);
      else await api.post("/contacts", payload);
      toast.success(isEdit ? "Contato atualizado" : "Contato adicionado");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-contact-${initial.id}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="gap-2" data-testid="add-contact-button"><Plus className="h-4 w-4" /> Novo contato</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar contato" : "Novo contato"}</DialogTitle>
          <DialogDescription>Dados da pessoa interessada em um PGD.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="contact-name-input" /></div>
          <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="contact-phone-input" /></div>
          <div>
            <Label>Grupo de interesse</Label>
            <Select value={form.group_id} onValueChange={(v) => set("group_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="contact-group-select"><SelectValue placeholder="Selecionar grupo" /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Mensagem / observação</Label><Textarea value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" data-testid="contact-message-input" /></div>
          <DialogFooter><Button type="submit" className="gap-2" data-testid="save-contact-button"><Check className="h-4 w-4" /> Salvar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [groupsArr, setGroupsArr] = useState([]);
  const groups = Object.fromEntries(groupsArr.map((g) => [g.id, g.name]));

  const load = useCallback(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/groups").then((r) => setGroupsArr(r.data)).catch(() => {});
  }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success("Contato removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="contacts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Contatos & Interessados</h1>
          <p className="text-muted-foreground mt-1">Pessoas que solicitaram participar de um PGD.</p>
        </div>
        <ContactForm groups={groupsArr} onSaved={load} />
      </div>

      {contacts.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
          Nenhuma solicitação de contato ainda.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {contacts.map((c) => (
            <Card key={c.id} className="p-6" data-testid="contact-card">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-semibold text-lg">{c.name}</h3>
                <Badge variant="secondary">{groups[c.group_id] || "Grupo"}</Badge>
              </div>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> {c.phone}</div>
                {c.message && <div className="flex items-start gap-2"><MessageSquare className="h-4 w-4 text-primary mt-0.5" /> {c.message}</div>}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : ""}</span>
                <div className="flex items-center gap-1">
                  <ContactForm groups={groupsArr} initial={c} onSaved={load} />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-contact-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remover {c.name}?</AlertDialogTitle>
                        <AlertDialogDescription>O contato será removido permanentemente.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(c.id)}>Remover</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
